/**
 * @m8t-stack/brain-engine — the seam-3 contract.
 * Additive-only; the dreamer and referee consume this exact shape.
 * LedgerRow + RoundEnrichment are owned by the agent-ledger read split —
 * imported here, never redefined.
 */
import type { LedgerRow, RoundEnrichment } from "@m8t-stack/agent-ledger/read";
/** Re-export the owned read types under the contract barrel — consumers reference them by CONTRACT name. */
export type { LedgerRow, RoundEnrichment } from "@m8t-stack/agent-ledger/read";

/** Pinned — stamped into the cursor; bumped only on a contract break. */
export const ENGINE_VERSION = "0.1.0";

/** Watermark trails real time by this margin (>longest plausible turn+emit latency). */
export const DEFAULT_SAFETY_LAG_SECONDS = 3600;

/** The closed source set; anything outside routes to `unknown-source`. */
export const SOURCES = ["webapp", "a2a", "mcp", "telegram", "voice-relay"] as const;

/**
 * Physical PK → canonical worker, for renames that want to keep historical attribution.
 *
 * EMPTY BY DECISION, not by accident. The rename to `ezra` deliberately did NOT alias
 * either of its predecessors, so ledger rows written under the older partition keys no
 * longer canonicalize onto the live worker: their attribution is ORPHANED, permanently.
 * That was ruled acceptable because no external install predates the rename and the
 * production instance is provisioned fresh with no history.
 *
 * The map itself stays as the seam a future rename would use. Orphaning is accepted;
 * THROWING IS NOT — a row bearing a legacy key must still read cleanly (see the tests).
 */
export const WORKER_ALIASES: Record<string, string> = {};

/**
 * Resolve a physical worker/PartitionKey to its canonical lowercase form: lowercase
 * first, then apply the alias map above (currently empty, so this is a lowercase).
 * The single resolver — cursor.ts and ledger-source.ts both import this. Pure.
 */
export function canonicalWorker(pk: string): string {
  const lower = pk.toLowerCase();
  return WORKER_ALIASES[lower] ?? lower;
}

/** The ordered, exhaustive, mutually-exclusive skip partition. */
export type SkipReason =
  | "not-target-worker"
  | "no-attribution"
  | "a2a-unlinked"
  | "no-conv-id"
  | "recovery-no-content"
  | "conv-fetch-failed"
  | "auth-failed"
  | "outside-trace-window"
  | "unknown-source";

export interface SkipEntry {
  reason: SkipReason;
  count: number;
  sampleIds: string[];
  lastError?: string;
}

/** Structured, NOT flattened — PII + misrouting need the source+ref split. */
export interface NormalizedUser {
  source: string;
  ref: string;
  display?: string;
}

export interface ToolUse {
  name: string;
  type: string;
  count: number;
  queries: string[];
  sources: string[];
}

/** A single transcript turn; `at` is optional until the timestamp doctrine lands. */
export interface NormalizedItem {
  role: string;
  text: string;
  at?: string;
}

export interface NormalizedConversation {
  conversationId: string;
  source: string;
  attribution: {
    worker: string;
    user?: NormalizedUser;
    caller?: string;
    target?: string;
    delegationId?: string;
  };
  startedAt: string;
  lastAt: string;
  rounds: number;
  outcome: string;
  /** Transcript, oldest-first (conversations.items.list is newest-first → reversed). */
  items: NormalizedItem[];
  enrichment?: { inputTokens: number; outputTokens: number; tools: ToolUse[] };
  recoveredVia?: "trace";
  /**
   * Source-row provenance — the physical PK + the storage RowKeys
   * (and their emit-times) of the LedgerRows this conversation was harvested from.
   * Stamped by runPipeline where normalize() is called; consumed by capCorpus to
   * recompute a PARTIAL cursor advance over only the KEPT conversations' rows. A
   * conversation groups ≥1 row, so `rows` is a SET (a2a/multi-round span many).
   * Optional only for the contract's additive guarantee — the harvest always sets it.
   */
  provenance?: { pk: string; rows: ConversationRow[] };
}

/** One source LedgerRow behind a conversation: its dedup RowKey + emit-time. The
 *  exact fields computeWatermark reads, so capCorpus re-derives the kept-set
 *  watermark with the SAME math as the full-corpus advance. */
export interface ConversationRow {
  rowKey: string;
  eventTimestamp: string;
}

/** Harvest metrics; `recovered` counts only content-proven recoveries. */
export interface DreamStats {
  ledgerRows: number;
  groups: number;
  consumed: number;
  recovered: number;
  skipped: number;
  latencyMs: number;
}

export interface NormalizedDreamInput {
  worker: string;
  window: { from?: string; to: string };
  conversations: NormalizedConversation[];
  skipLedger: SkipEntry[];
  stats: DreamStats;
  /** The write-gated cursor advance. Computed every run; committed by runDream only after a successful brain write. */
  advance: AdvancePlan;
}

/** Per-physical-PK watermark. `ts` = min(max(consumed emit-time), now − safetyLag). */
export interface PkWatermark {
  ts: string;
  consumedRowKeys: string[];
}

/** One cursor row per canonical worker; `watermarks` keyed by physical PK. */
export interface DreamCursor {
  worker: string;
  watermarks: Record<string, PkWatermark>;
  fetchFailures?: Record<string, number>;
  safetyLagSeconds: number;
  engineVersion: string;
  lastDreamAt?: string;
}

/**
 * Write-gated cursor advance. runPipeline
 * COMPUTES this; runDream COMMITS it via commitCursorAdvance ONLY after the brain
 * write succeeds. Dry-run still computes it (for the would-advance preview) but
 * never commits. Carries the per-PK watermarks + the rows consumed this run +
 * the fetch-error fence so the commit reproduces the exact same advance.
 */
export interface AdvancePlan {
  worker: string;
  watermarks: Record<string, PkWatermark>;     // per physical PK
  consumedRowsByPk: Record<string, string[]>;   // rowKeys consumed this run (overlap-dedup already correct)
  failedTsByPk: Record<string, string>;         // PK -> earliest fetch-errored ts (the fence; never advance past it)
  fetchFailures?: Record<string, number>;       // conversationId -> committed-cycle count to persist next
  /**
   * The inputs buildAdvancePlan used, carried so capCorpus can RE-derive a partial
   * advance over only the kept conversations' rows via the SAME computeWatermark +
   * monotonic re-pin + fence — never by reusing the full-corpus ts
   * with a filtered key list (which would leave ts/consumedRowKeys inconsistent).
   * Optional only for the contract's additive guarantee; runPipeline always sets them.
   */
  now?: string;                                 // the injected clock (ISO) the watermark was clamped against
  safetyLagSeconds?: number;                    // the lag used for the (now − lag) clamp + overlap floor
  priorTsByPk?: Record<string, string>;         // the committed prior watermark ts per PK (the monotonic re-pin floor)
}

/** Injected source interfaces — the seam. Concrete impls built at the entry point; tests inject fakes. */
export interface LedgerSource {
  /** PK-scoped (worker + frozen alias), watermark+overlap; auto-paginated, no `$top`. */
  rowsSince(physicalPks: string[], cursor: DreamCursor): Promise<LedgerRow[]>;
}

export interface ConversationSource {
  /** Throws ConvFetchError on 404/terminal; returns transcript items. */
  items(conversationId: string): Promise<NormalizedItem[]>;
  /** The a2a recovery gate's app-tag read; null = un-provable → caller hard-gates it out. */
  retrieveApp?(conversationId: string): Promise<string | null>;
}

export interface TraceSource {
  enrich(
    respIds: string[],
    window: { from: string; to: string },
  ): Promise<Map<string, RoundEnrichment>>;
  /** Returns a validated `conv_` id, or null if outside-window / not recoverable. */
  recoverConversationId(
    respId: string,
    window: { from: string; to: string },
  ): Promise<string | null>;
}

export interface Cursor {
  /** Tolerates a missing table → absent-cursor (from-the-beginning); never creates the table. */
  read(worker: string): Promise<DreamCursor>;
  /** NON-dry-run only (dreamer path); never advances past a fetch-errored row. */
  advance(worker: string, cursor: DreamCursor): Promise<void>;
}

/** Terminal transcript-fetch failure; carries the disposition kind. */
export class ConvFetchError extends Error {
  constructor(
    public kind: "not-found" | "terminal" | "auth",
    message: string,
  ) {
    super(message);
    this.name = "ConvFetchError";
  }
}
